import { selectNode } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { buildSizes } from "@common/build/constants";
import { useEffect, useRef, useState } from "react";
import "./devices.scss";
import "./style.scss";
import "./extra_devices/samsung-galaxy.scss";
import "./extra_devices/samsung-kiosk.scss";
import "./extra_devices/google-pixel.scss";
import "./extra_devices/iphone-15.scss";
import "./extra_devices/iphone-14.scss";
import "./extra_devices/ipad-large-flat.scss";
import "./extra_devices/ipad-mini.scss";
import "./extra_devices/imac.scss";

interface DeviceSkinProps {
    children: React.ReactNode;
}

const DeviceSkin = ({ children }: DeviceSkinProps) => {
    const frameSize = useAppSelector(state => state.builder.frameSize);
    const buildSize = useAppSelector(state => state.builder.build.size);
    const frameSizeDetails = buildSizes[buildSize.key] || {};
    const isLandscape = typeof frameSize.y === "number" && frameSize.x > frameSize.y;
    const skinRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const [currentTime, setCurrentTime] = useState(new Date());

    const getDeviceClass = () => {
        const classes = [];
        const deviceType = frameSizeDetails.type?.toLowerCase() || "mobile";
        classes.push(deviceType);
        frameSizeDetails.class && classes.push(frameSizeDetails.class);
        deviceType === "desktop" || (isLandscape && classes.push("landscape"));
        return classes.join(" ");
    };

    useEffect(() => {
        dispatch(selectNode(null));
    }, []);

    useEffect(() => {
        // Here is a super unecessary feature to update the presented time in the device every five secs
        const intervalId = setInterval(() => {
            setCurrentTime(new Date());
        }, 5000);

        setCurrentTime(new Date());

        return () => clearInterval(intervalId);
    }, []);

    useEffect(() => {
        const device = skinRef.current;
        if (device) {
            const width = frameSize.x;
            const height = typeof frameSize.y === "number" ? frameSize.y : width;
            const paddingBlock = 80;
            const paddingInline = 40;
            const wrapper = wrapperRef.current;

            device.style.setProperty("--custom-device-width", `${width}px`);
            device.style.setProperty("--custom-device-height", `${height}px`);

            if (wrapper) {
                const wrapperWidth = wrapper.clientWidth - 2 * paddingInline;
                const wrapperHeight = wrapper.clientHeight - 2 * paddingBlock;

                const scaleX = wrapperWidth / width;
                const scaleY = wrapperHeight / height;
                const scale = Math.min(scaleX, scaleY, 1);

                wrapper.style.setProperty("scale", scale.toString());
            }
        }
    }, [frameSize]);

    return (
        <div className="device-skin-wrapper" ref={wrapperRef}>
            <div className="device-skin">
                <div className={`css-device-skin black ${getDeviceClass()}`} ref={skinRef}>
                    <div className="top-bar" />
                    <div className="sleep" />
                    <div className="volume" />
                    <div className="notch">
                        <div className="camera" />
                        <div className="speaker" />
                    </div>

                    <div className="overflow">
                        <div className="shadow shadow--tr" />
                        <div className="shadow shadow--tl" />
                        <div className="shadow shadow--br" />
                        <div className="shadow shadow--bl" />
                    </div>
                    <div className="inner-shadow" />

                    <div className="sensor" />
                    <div className="speaker" />

                    <div className="screen">
                        <div className="status-bar">
                            <div className="hour">
                                {currentTime.getHours()}:{currentTime.getMinutes().toString().padStart(2, "0")}
                            </div>
                            <div className="inner-icons">
                                <img src="/skins-network.svg" alt="" />
                                <div className="battery" />
                            </div>
                        </div>

                        {children}
                        <div className="screen-buttons" />
                    </div>
                    <div className="home" />
                    <div className="bottom-bar" />
                    <div className="device-power" />
                    <div className="device-stand">
                        <div className="kiosk-stuff" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeviceSkin;
